import {
  FlexRowCenterBoth,
  INVERTED_TRANSLATE_LOCALE,
  LoadingSpinner,
  matchLanguages,
} from '@main/core-ui';
import { DEFAULT_LOCALE } from '@main/internationalization';
import { PAGE_PATHNAMES } from '@main/pc-types';
import NetworkError from '@main/privacy-center/src/components/NetworkError';
import AboutTranscendPage from '@main/privacy-center/src/containers/AboutTranscendPage/Loadable';
import App from '@main/privacy-center/src/containers/App';
import { usePrivacyCenterData } from '@main/privacy-center/src/containers/App/usePrivacyCenter';
import { ErrorWrapper } from '@main/privacy-center/src/containers/App/wrappers';
import NotFound from '@main/privacy-center/src/containers/NotFound/Loadable';
import OAuthCallback from '@main/privacy-center/src/containers/OAuthCallback/Loadable';
import TokenLogin from '@main/privacy-center/src/containers/TokenLogin/Loadable';
import PAGES from '@main/privacy-center/src/pages';
import { PRIVACY_CENTER_THEME } from '@main/privacy-center/src/theme';
import { isKnownLocale } from '@transcend-io/internationalization';
import { getKeys } from '@transcend-io/type-utils';
import React, { useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { Route, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { HeroPage } from './HeroPage/HeroPage';
import { LangProvider } from './LangProvider';
import { LocalizedSwitch } from './LocalizedSwitch';

/**
 * Manages all routes for the application
 */
export const PrivacyCenterRoutes: React.FC = () => {
  const { pathname } = useLocation();
  const { error, privacyCenter, isDisabled } = usePrivacyCenterData();
  const navigate = useNavigate();
  const potentialLocale = (
    pathname.endsWith('/') ? pathname.slice(0, pathname.length - 1) : pathname
  )
    .split('/')
    .pop()!;

  // uppercase dialect for requested language
  const formattedPotentialLocale = potentialLocale.includes('-')
    ? potentialLocale
        .split('-')
        .map((part, index) => (index === 1 ? part.toUpperCase() : part))
        .join('-')
    : // needed for isKnownLocale check in useEffect
      INVERTED_TRANSLATE_LOCALE[potentialLocale]
      ? INVERTED_TRANSLATE_LOCALE[potentialLocale][0]
      : undefined;

  // match supported languages with browser languages
  const matchedBrowserLanguages = privacyCenter?.preferBrowserDefaultLocale
    ? matchLanguages(navigator.languages, privacyCenter.locales)
    : undefined;

  const matchedRequestedLanguage =
    privacyCenter?.locales && formattedPotentialLocale
      ? matchLanguages([formattedPotentialLocale], privacyCenter.locales)[0]
      : undefined;

  const privacyCenterDefaultLocale = privacyCenter?.defaultLocale
    ? privacyCenter.defaultLocale
    : DEFAULT_LOCALE;

  const locale =
    matchedRequestedLanguage ||
    (privacyCenter?.preferBrowserDefaultLocale &&
    matchedBrowserLanguages &&
    matchedBrowserLanguages?.length > 0
      ? matchedBrowserLanguages[0]
      : privacyCenterDefaultLocale);

  useEffect(() => {
    // redirect if locale is different from requested language
    // or requested language is supported but incorrectly formatted
    // (eg. en-us instead of en-US)
    if (
      privacyCenter &&
      !isDisabled &&
      isKnownLocale(formattedPotentialLocale) && // needed to prevent weird behavior
      (locale !== formattedPotentialLocale ||
        potentialLocale !== formattedPotentialLocale)
    ) {
      const newPath = pathname.split('/');
      if (!pathname.endsWith('/')) {
        newPath.pop();
      } else {
        newPath.splice(newPath.length - 2, 1);
      }
      newPath.push(locale);
      navigate(newPath.filter((val, i) => i === 0 || val !== '').join('/'));
    }
  }, [
    privacyCenter,
    locale,
    formattedPotentialLocale,
    navigate,
    pathname,
    potentialLocale,
    isDisabled,
  ]);

  // The privacy center URL cannot be found in the db
  const invalidPrivacyCenterError = error?.message.includes(
    'The privacy center referer URL is not recognized',
  )
    ? error
    : undefined;

  // The server is down
  const networkError = error?.message.includes('Network error:')
    ? error
    : undefined;

  // Determine if error should be rendered
  const displayError = networkError ? (
    <NetworkError />
  ) : invalidPrivacyCenterError ? (
    <NotFound />
  ) : error ? (
    <NetworkError /> // ideally has its own component
  ) : isDisabled ? (
    // eslint-disable-next-line max-len
    <NotFound message="This Privacy Center is disabled! If you are an admin, please re-enable the Privacy Center to use this page." />
  ) : null;

  // Render full screen error
  if (!privacyCenter && displayError) {
    return (
      <IntlProvider locale={locale} messages={{}}>
        <ThemeProvider theme={PRIVACY_CENTER_THEME}>
          <ErrorWrapper>{displayError}</ErrorWrapper>
        </ThemeProvider>
      </IntlProvider>
    );
  }

  // Render for page loading
  if (!privacyCenter) {
    return (
      <FlexRowCenterBoth>
        <LoadingSpinner />
      </FlexRowCenterBoth>
    );
  }

  return (
    <LangProvider lang={locale}>
      <App>
        <LocalizedSwitch>
          {
            /** Page definitions */
            getKeys(PAGES).map((pageType) => {
              const Component = PAGES[pageType];
              return (
                <Route
                  key={pageType}
                  element={
                    <HeroPage>
                      <Component />
                    </HeroPage>
                  }
                  path={PAGE_PATHNAMES[pageType]}
                />
              );
            })
          }
          {/** About Transcend page */}
          <Route element={<AboutTranscendPage />} path="/about" />
          {/** OAuth Callback */}
          <Route
            element={
              <HeroPage>
                <OAuthCallback />
              </HeroPage>
            }
            path="/oauth-callback"
          />
          <Route
            element={
              <HeroPage>
                <TokenLogin />
              </HeroPage>
            }
            path="/login"
          />
          {/** Not found */}
          <Route
            element={
              <HeroPage>
                <NotFound />
              </HeroPage>
            }
            path="*"
          />
        </LocalizedSwitch>
      </App>
    </LangProvider>
  );
};
export default PrivacyCenterRoutes;
